import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Alert, Badge, Dropdown } from 'react-bootstrap';
import { Plus, Edit, Trash2, Search, Mail, FileText, CheckCircle, XCircle, Clock, CreditCard, AlertCircle } from 'lucide-react';
import { invoiceAPI, clientAPI, productAPI } from '../services/api';

const API_BASE_URL = 'https://backfacturas.rmtagency.es/api';

interface Client {
  id: number;
  name: string;
  email: string;
  taxId: string;
}

interface Product {
  id: number;
  name: string;
  price: number;
  taxRate: number;
  unit: string;
}

interface InvoiceItem {
  id?: number;
  product?: Product;
  productName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  lineTotal: number;
}

interface Invoice {
  id: number;
  client: Client;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  paymentMethod: string;
  notes: string;
  items: InvoiceItem[];
  createdAt: string;
  updatedAt: string;
}

const Facturas: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    clientId: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    paymentMethod: '',
    notes: '',
    status: 'BORRADOR',
    items: [] as InvoiceItem[]
  });

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchProducts();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getAll();
      setInvoices(response.data);
    } catch (err) {
      setError('Error al cargar las facturas');
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getActive();
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const invoiceData = {
        ...formData,
        clientId: parseInt(formData.clientId),
        items: formData.items.map(item => ({
          ...item,
          productId: item.product?.id
        }))
      };
      
      if (editingInvoice) {
        await invoiceAPI.update(editingInvoice.id, invoiceData);
        // Update status if it changed
        if (formData.status !== editingInvoice.status) {
          await invoiceAPI.updateStatus(editingInvoice.id, formData.status);
        }
      } else {
        await invoiceAPI.create(invoiceData);
      }
      setShowModal(false);
      setEditingInvoice(null);
      resetForm();
      fetchInvoices();
    } catch (err) {
      setError('Error al guardar la factura');
      console.error('Error saving invoice:', err);
    }
  };

  const handleEdit = (invoice: Invoice) => {
    console.log('handleEdit called with invoice:', invoice);
    console.log('Original items count:', invoice.items?.length);
    console.log('Original items:', invoice.items);
    
    setEditingInvoice(invoice);
    
    const mappedItems = invoice.items ? invoice.items.map(item => ({
      ...item,
      product: item.product || undefined
    })) : [];
    
    console.log('Mapped items count:', mappedItems.length);
    console.log('Mapped items:', mappedItems);
    
    setFormData({
      clientId: invoice.client.id.toString(),
      invoiceNumber: invoice.invoiceNumber || '',
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      paymentMethod: invoice.paymentMethod || '',
      notes: invoice.notes || '',
      status: invoice.status || 'BORRADOR',
      items: mappedItems
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta factura?')) {
      try {
        await invoiceAPI.delete(id);
        fetchInvoices();
      } catch (err) {
        setError('Error al eliminar la factura');
        console.error('Error deleting invoice:', err);
      }
    }
  };

  const handleUpdateStatus = async (invoiceId: number, newStatus: string) => {
    try {
      await invoiceAPI.updateStatus(invoiceId, newStatus);
      fetchInvoices();
    } catch (err) {
      setError('Error al actualizar el estado de la factura');
      console.error('Error updating invoice status:', err);
    }
  };

  const handleSendEmail = async (id: number) => {
    try {
      await invoiceAPI.sendEmail(id);
      alert('Factura enviada por email correctamente');
    } catch (err) {
      setError('Error al enviar la factura por email');
      console.error('Error sending invoice email:', err);
    }
  };

  const handleGeneratePDF = async (id: number) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Ensure we use the same protocol as the current page
      const apiBaseUrl = 'https://backfacturas.rmtagency.es/api';
      
      const url = `${apiBaseUrl}/invoices/${id}/pdf`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/pdf'
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `factura-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error('Error al descargar el PDF');
      }
    } catch (err) {
      setError('Error al generar el PDF');
      console.error('Error generating PDF:', err);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        productName: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        taxRate: 21,
        lineTotal: 0
      }]
    });
  };

  const removeItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...formData.items];
    if (field === 'product') {
      const product = products.find(p => p.id === parseInt(value));
      if (product) {
        newItems[index] = {
          ...newItems[index],
          product,
          productName: product.name,
          unitPrice: product.price,
          taxRate: product.taxRate
        };
      }
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    
    // Calculate line total
    const item = newItems[index];
    item.lineTotal = item.quantity * item.unitPrice;
    
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.lineTotal, 0);
    const taxAmount = formData.items.reduce((sum, item) => 
      sum + (item.lineTotal * item.taxRate / 100), 0);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const resetForm = () => {
    setFormData({
      clientId: '',
      invoiceNumber: '',
      invoiceDate: '',
      dueDate: '',
      paymentMethod: '',
      notes: '',
      status: 'BORRADOR',
      items: []
    });
  };

  const handleNewInvoice = () => {
    setEditingInvoice(null);
    resetForm();
    setShowModal(true);
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'BORRADOR': 'secondary',
      'ENVIADA': 'info',
      'PAGADA': 'success',
      'ESPERANDO_PAGO': 'warning',
      'CANCELADA': 'dark'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Gestión de Facturas</h4>
              <Button variant="primary" onClick={handleNewInvoice}>
                <Plus size={20} className="me-2" />
                Nueva Factura
              </Button>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Buscar facturas..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search size={20} className="position-absolute top-50 end-0 translate-middle-y me-3" />
                  </Form.Group>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Cliente</th>
                      <th>Fecha</th>
                      <th>Vencimiento</th>
                      <th>Total</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.invoiceNumber}</td>
                        <td>{invoice.client.name}</td>
                        <td>{new Date(invoice.invoiceDate).toLocaleDateString('es-ES')}</td>
                        <td>{new Date(invoice.dueDate).toLocaleDateString('es-ES')}</td>
                        <td>{formatPrice(invoice.total)}</td>
                        <td>{getStatusBadge(invoice.status)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEdit(invoice)}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleGeneratePDF(invoice.id)}
                            >
                              <FileText size={16} />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleSendEmail(invoice.id)}
                            >
                              <Mail size={16} />
                            </Button>
                            
                            
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(invoice.id)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear/editar factura */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingInvoice ? 'Editar Factura' : 'Nueva Factura'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    required
                    value={formData.clientId}
                    onChange={(e) => setFormData({...formData, clientId: e.target.value})}
                  >
                    <option value="">Seleccionar cliente...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} - {client.taxId}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Factura</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Auto-generado"
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
                  />
                  <Form.Text className="text-muted">
                    Si se deja vacío, se generará automáticamente
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Factura *</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={formData.invoiceDate}
                    onChange={(e) => setFormData({...formData, invoiceDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Vencimiento *</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={formData.dueDate}
                    onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Líneas de Factura</h5>
              <Button variant="outline-primary" size="sm" onClick={addItem}>
                <Plus size={16} className="me-1" />
                Añadir Línea
              </Button>
            </div>

            {(() => {
              console.log('About to render items. FormData items count:', formData.items.length);
              console.log('FormData items:', formData.items);
              return formData.items.map((item: InvoiceItem, index: number) => (
              <Row key={index} className="align-items-end mb-2 p-2 border rounded">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Producto</Form.Label>
                    <Form.Select
                      value={item.product?.id || ''}
                      onChange={(e) => updateItem(index, 'product', e.target.value)}
                    >
                      <option value="">Producto personalizado...</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - {formatPrice(product.price)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Nombre</Form.Label>
                    <Form.Control
                      type="text"
                      value={item.productName}
                      onChange={(e) => updateItem(index, 'productName', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group>
                    <Form.Label>Cant.</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Precio</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group>
                    <Form.Label>IVA %</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.taxRate}
                      onChange={(e) => updateItem(index, 'taxRate', parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Total</Form.Label>
                    <Form.Control
                      type="text"
                      readOnly
                      value={formatPrice(item.lineTotal)}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeItem(index)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </Col>
              </Row>
            ));
            })()}

            <Row className="mt-3">
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Método de Pago</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                  />
                </Form.Group>
                
                {editingInvoice && (
                  <Form.Group className="mb-3">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                    >
                      <option value="BORRADOR">Borrador</option>
                      <option value="ENVIADA">Enviada</option>
                      <option value="PAGADA">Pagada</option>
                      <option value="ESPERANDO_PAGO">Esperando Pago</option>
                      <option value="CANCELADA">Cancelada</option>
                    </Form.Select>
                  </Form.Group>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Card>
                  <Card.Body>
                    <div className="d-flex justify-content-between">
                      <span>Subtotal:</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>IVA:</span>
                      <span>{formatPrice(taxAmount)}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold">
                      <span>Total:</span>
                      <span>{formatPrice(total)}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingInvoice ? 'Actualizar' : 'Crear'} Factura
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Facturas;